import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { memo, useMemo } from 'react';
import { Link, useHistory } from 'react-router-dom';
import { compact } from 'lodash-es';
import { InvoicePaymentDisplayStatusType } from 'anchor-scout-common/types';
import { TableListRow, Typography, TableListActions, DropdownMenuItem, ConfirmationModal, WithInfoIcon, Row } from 'seagull';
import { DocumentCheckmark, Download, EmailSent, Eye, LinkIcon, MoneyReceive, Redo, Void } from 'seagull/icons';
import { allowResendAction, allowUnmarkPaidExternallyAction, allowUnvoidAction, allowVoidAction, getPaymentStatusText, getStatusLabel, getStatusV2Label, getPaymentDetailTooltip, formatPaymentDetailsList } from '~/business-logic/invoiceLogic';
import { InvoiceAccountingIntegrationStatus } from '~/components/InvoiceAccountingIntegrationStatus';
import { routes } from '~/constants';
import { useOverlayContext, useStore, useStoreActions } from '~/contexts';
import { useDownloadInvoicePdf } from '~/hooks/invoice/useDownloadInvoicePdf';
import { useCopyDirectLink } from '~/hooks/useCopyDirectLink';
import { usePaidExternally } from '~/hooks/usePaidExternally';
import { useResendInvoice } from '~/hooks/useResendInvoice';
import { useLocaleKeys } from '~/i18n';
import { Intent } from '~/infra/access-control';
import { invoiceModel, timeModel } from '~/models';
import { selectors } from '~/store/state-selectors';
import { Accounting } from '~/types/api.model';
import styles from './InvoicesTable.module.scss';
const voidLoader = 'void';
const displayedStatusToSkin = {
    issued: undefined,
    overdue: 'error',
    paymentInitiated: 'success',
    paid: 'success',
    voided: undefined,
    refunded: 'standard',
    refundFailed: 'error',
    disputed: 'error',
    partiallyRefunded: 'standard'
};
const displayedStatusV2ToSkin = {
    issued: undefined,
    pending: undefined,
    overdue: 'error',
    processing: 'success',
    paid: 'success',
    voided: undefined,
    refunded: 'standard',
    refundFailed: 'error',
    disputed: 'error',
    partiallyRefunded: 'standard'
};
function getPaymentStatusTooltip(displayedPaymentMethodStatus) {
    if (!displayedPaymentMethodStatus) {
        return '';
    } else {
        switch(displayedPaymentMethodStatus.type){
            case InvoicePaymentDisplayStatusType.Pending:
                return 'Automatic payment processing will begin shortly';
            case InvoicePaymentDisplayStatusType.Processing:
                return 'Processing the payment may take up to 3 business days';
            case InvoicePaymentDisplayStatusType.Collected:
                return "The payment will be deposited to your bank account within 1-2 business days";
            case InvoicePaymentDisplayStatusType.RefundRequested:
                return 'Upon completion of payment processing, a refund will be issued';
            default:
                return '';
        }
    }
}
function getPaymentStatusSkin(displayedPaymentMethodStatus) {
    if (!displayedPaymentMethodStatus) {
        return undefined;
    } else {
        switch(displayedPaymentMethodStatus.type){
            case InvoicePaymentDisplayStatusType.PaymentMethodNotSet:
            case InvoicePaymentDisplayStatusType.PaymentFailed:
            case InvoicePaymentDisplayStatusType.StoppedPayment:
                return 'error';
            case InvoicePaymentDisplayStatusType.PaymentMethodUnverified:
            case InvoicePaymentDisplayStatusType.RequiresPaymentApproval:
            case InvoicePaymentDisplayStatusType.RequiresPaymentValidation:
                return 'warning';
            default:
                return undefined;
        }
    }
}
function InvoicesTableRow(props) {
    const { t: texts } = useLocaleKeys('invoice.tableRow');
    const { t: commonTexts } = useLocaleKeys('common');
    const { t: invoiceTexts } = useLocaleKeys('invoice');
    const { invoice, hideDueDate, showQboIntegration, showXeroIntegration, INVOICE_STATUSES_V2, to, retry, onUnvoidInvoice, onUnmarkPaidExternally } = props;
    const overlayContext = useOverlayContext();
    const { markAsPaid, shouldShowMarkAsPaid } = usePaidExternally();
    const { resendInvoice } = useResendInvoice();
    const history = useHistory();
    const isVendor = useStore(selectors.user.isVendor);
    const email = useStore(selectors.user.email);
    const testId = "invoice-row";
    const { api, logBI } = useStoreActions();
    const { copyDirectLink } = useCopyDirectLink();
    const { downloadInvoicePdf } = useDownloadInvoicePdf();
    const paymentStatusTooltip = getPaymentStatusTooltip(invoice.paymentDisplayStatus);
    const paymentStatusText = useMemo(()=>{
        return getPaymentStatusText(invoice, isVendor);
    }, [
        invoice,
        isVendor
    ]);
    const onVoidInvoice = ()=>{
        overlayContext.showModal({
            closeOnClickOutside: true,
            closeOnEscapeKey: true,
            openBottomSheetOnMobile: true,
            hideBottomSheetOnDismiss: true,
            content: (param)=>{
                let { dismissModal } = param;
                return /*#__PURE__*/ _jsx(ConfirmationModal, {
                    title: texts.voidTitle(),
                    content: texts.voidConfirmation({
                        invoiceNumber: invoice.formattedNumber
                    }),
                    testId: "void-confirmation-modal",
                    primaryButtonLabel: commonTexts.buttons.imSure(),
                    secondaryButtonLabel: commonTexts.buttons.cancel(),
                    onSecondaryButtonClick: ()=>dismissModal(),
                    onPrimaryButtonClick: async ()=>{
                        try {
                            overlayContext.startLoader(voidLoader);
                            await api.invoice.void(invoice.id);
                            await retry();
                            dismissModal(true);
                        } catch (error) {
                            overlayContext.showErrorToast(commonTexts.toasts.unableToVoidInvoice(), 'void-invoice-error');
                            dismissModal();
                        } finally{
                            overlayContext.stopLoader(voidLoader);
                        }
                    }
                });
            }
        });
    };
    return /*#__PURE__*/ _jsx(TableListRow, {
        as: Link,
        to: to,
        size: "large",
        testId: testId,
        "data-heapid": "invoice",
        items: compact([
            {
                className: styles.invoiceIdCell,
                value: /*#__PURE__*/ _jsxs(Row, {
                    alignItems: "center",
                    gap: 8,
                    children: [
                        /*#__PURE__*/ _jsx(Typography, {
                            testId: "".concat(testId, "-invoice-no"),
                            skin: "secondary",
                            className: styles.invoiceId,
                            children: invoice.formattedNumber
                        }),
                        showQboIntegration && /*#__PURE__*/ _jsx(InvoiceAccountingIntegrationStatus, {
                            testId: "".concat(testId, "-qbo"),
                            invoiceIntegration: invoice.integration,
                            accountingType: Accounting.QBO
                        }),
                        showXeroIntegration && /*#__PURE__*/ _jsx(InvoiceAccountingIntegrationStatus, {
                            testId: "".concat(testId, "-xero"),
                            invoiceIntegration: invoice.xeroIntegration.getIntegration(),
                            accountingType: Accounting.Xero
                        })
                    ]
                })
            },
            {
                value: /*#__PURE__*/ _jsxs(Typography, {
                    ellipsis: true,
                    weight: "normal",
                    testId: "".concat(testId, "-comp-name"),
                    children: [
                        invoiceModel.otherPartyCompanyName(invoice, isVendor),
                        "\xa0"
                    ]
                })
            },
            {
                value: INVOICE_STATUSES_V2 && invoice.displayStatusV2 ? /*#__PURE__*/ _jsx(Typography, {
                    skin: displayedStatusV2ToSkin[invoice.displayStatusV2],
                    weight: "normal",
                    testId: "".concat(testId, "-status"),
                    children: getStatusV2Label(invoice.displayStatusV2)
                }) : /*#__PURE__*/ _jsx(Typography, {
                    skin: displayedStatusToSkin[invoice.displayStatus],
                    weight: "normal",
                    testId: "".concat(testId, "-status"),
                    children: getStatusLabel(invoice.displayStatus)
                })
            },
            {
                value: INVOICE_STATUSES_V2 && invoice.paymentDetails ? invoice.paymentDetails.length > 0 ? /*#__PURE__*/ _jsx(WithInfoIcon, {
                    className: styles.paymentDetailsCell,
                    info: getPaymentDetailTooltip(invoice.paymentDetails),
                    showInfo: !!getPaymentDetailTooltip(invoice.paymentDetails),
                    children: /*#__PURE__*/ _jsx(Typography, {
                        ellipsis: true,
                        testId: "".concat(testId, "-payment-details"),
                        children: formatPaymentDetailsList(invoice.paymentDetails)
                    })
                }) : null : paymentStatusText && /*#__PURE__*/ _jsx(WithInfoIcon, {
                    info: paymentStatusTooltip,
                    showInfo: !!paymentStatusTooltip,
                    children: /*#__PURE__*/ _jsx(Typography, {
                        skin: getPaymentStatusSkin(invoice.paymentDisplayStatus),
                        testId: "".concat(testId, "-payment-status"),
                        children: paymentStatusText
                    })
                })
            },
            {
                value: /*#__PURE__*/ _jsx(Typography, {
                    skin: "secondary",
                    testId: "".concat(testId, "-issued-on"),
                    children: INVOICE_STATUSES_V2 ? timeModel.formatDateLong(invoice.rawIssuedOn) : invoiceModel.issueDate(invoice)
                })
            },
            !hideDueDate && {
                value: /*#__PURE__*/ _jsx(Typography, {
                    skin: "secondary",
                    testId: "".concat(testId, "-due-date"),
                    children: INVOICE_STATUSES_V2 ? timeModel.formatDateLong(invoice.rawDueDate) : invoice.dueDate
                })
            },
            {
                value: /*#__PURE__*/ _jsx(Typography, {
                    weight: "light",
                    skin: "secondary",
                    testId: "".concat(testId, "-amount"),
                    ellipsis: true,
                    children: invoice.totalAmount
                })
            }
        ]),
        actionColumn: /*#__PURE__*/ _jsxs(TableListActions, {
            buttonText: texts.view(),
            buttonIcon: /*#__PURE__*/ _jsx(Eye, {}),
            onClickButton: ()=>history.push(to),
            testId: testId,
            btnHeapId: "invoice-view-btn",
            menuHeapId: "invoice-menu-btn",
            mobile: {
                title: invoice.formattedNumber,
                subtitle: invoiceModel.otherPartyCompanyName(invoice, isVendor)
            },
            children: [
                shouldShowMarkAsPaid(invoiceModel.statusType(invoice), isVendor) && /*#__PURE__*/ _jsx(DropdownMenuItem, {
                    intent: Intent.InvoiceMarkPaidExternally,
                    testId: "".concat(testId, "-paid-externally"),
                    "data-heapid": "invoice-menu-paid-externally",
                    iconStart: /*#__PURE__*/ _jsx(MoneyReceive, {}),
                    onClick: ()=>markAsPaid(invoice, retry),
                    bi: {
                        name: 'mark_paid_externally'
                    },
                    children: commonTexts.buttons.markAsPaidExternally()
                }),
                allowResendAction(invoiceModel.statusType(invoice), isVendor) && /*#__PURE__*/ _jsx(DropdownMenuItem, {
                    intent: Intent.InvoiceResend,
                    testId: "".concat(testId, "-resend-invoice"),
                    "data-heapid": "invoice-menu-resend-invoice",
                    iconStart: /*#__PURE__*/ _jsx(EmailSent, {}),
                    onClick: ()=>{
                        resendInvoice(invoice).then(retry);
                    },
                    bi: {
                        name: 'resend_invoice'
                    },
                    children: commonTexts.buttons.resendInvoice()
                }),
                /*#__PURE__*/ _jsx(DropdownMenuItem, {
                    intent: Intent.InvoiceDownload,
                    testId: "".concat(testId, "-download-invoice"),
                    "data-heapid": "invoice-menu-download-invoice",
                    iconStart: /*#__PURE__*/ _jsx(Download, {}),
                    onClick: ()=>downloadInvoicePdf(invoice.id),
                    disabled: !invoice.pdfExists,
                    tooltipProps: invoice.pdfExists ? undefined : {
                        title: invoiceTexts.generatingPdf()
                    },
                    bi: {
                        name: 'download_invoice'
                    },
                    children: commonTexts.buttons.downloadInvoice()
                }),
                /*#__PURE__*/ _jsx(DropdownMenuItem, {
                    testId: "".concat(testId, "-agreement"),
                    "data-heapid": "invoice-menu-agreement",
                    as: Link,
                    to: routes.agreement(invoiceModel.relationshipId(invoice)),
                    iconStart: /*#__PURE__*/ _jsx(DocumentCheckmark, {}),
                    bi: {
                        name: 'view_agreement'
                    },
                    children: commonTexts.buttons.viewAgreement()
                }),
                allowVoidAction(invoiceModel.statusType(invoice), isVendor) && /*#__PURE__*/ _jsx(DropdownMenuItem, {
                    intent: Intent.InvoiceVoid,
                    iconStart: /*#__PURE__*/ _jsx(Void, {}),
                    onClick: onVoidInvoice,
                    testId: "".concat(testId, "-void"),
                    "data-heapid": "invoice-menu-void",
                    bi: {
                        name: 'void_invoice'
                    },
                    children: commonTexts.buttons.voidInvoice()
                }),
                allowUnvoidAction(invoice, isVendor) && /*#__PURE__*/ _jsx(DropdownMenuItem, {
                    intent: Intent.InvoiceUnvoid,
                    iconStart: /*#__PURE__*/ _jsx(Redo, {}),
                    onClick: onUnvoidInvoice,
                    testId: "".concat(testId, "-unvoid"),
                    "data-heapid": "invoice-menu-unvoid",
                    bi: {
                        name: 'unvoid_invoice'
                    },
                    children: commonTexts.buttons.unvoidInvoice()
                }),
                allowUnmarkPaidExternallyAction(invoice, isVendor) && /*#__PURE__*/ _jsx(DropdownMenuItem, {
                    intent: Intent.InvoiceUnmarkPaidExternally,
                    iconStart: /*#__PURE__*/ _jsx(Redo, {}),
                    onClick: onUnmarkPaidExternally,
                    testId: "".concat(testId, "-unmark-paid-externally"),
                    "data-heapid": "invoice-menu-unmark-paid-externally",
                    bi: {
                        name: 'unmark_paid_externally'
                    },
                    children: commonTexts.buttons.unmarkAsPaidExternally()
                }),
                /*#__PURE__*/ _jsx(DropdownMenuItem, {
                    intent: Intent.InvoiceCopyLink,
                    testId: "".concat(testId, "-copy-link"),
                    "data-heapid": "invoice-menu-copy-link",
                    iconStart: /*#__PURE__*/ _jsx(LinkIcon, {}),
                    bi: {
                        name: 'copy_link'
                    },
                    onClick: ()=>{
                        if (isVendor) {
                            logBI(14001, {
                                email
                            });
                        } else {
                            logBI(14003);
                        }
                        copyDirectLink(invoiceModel.relationshipId(invoice), {
                            type: 'invoice',
                            id: invoice.id
                        });
                    },
                    children: commonTexts.buttons.copyLink()
                })
            ]
        })
    }, invoice.id);
}
export default /*#__PURE__*/ memo(InvoicesTableRow);
